import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { automunityAPI } from '../lib/api';
import toast, { Toaster } from 'react-hot-toast';
import './Settings.css';

interface Badge {
  badge_id: number;
  name: string;
  description?: string;
  icon?: string;
  category?: string;
  date_entered?: string;
}

const UserProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<any>({});

  useEffect(() => {
    fetchUser();
    fetchUserBadges();
  }, [id]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const response = await automunityAPI.getUser(id!);
      const data = await response.body.json();
      const userData = (data as any)?.records?.[0] || (Array.isArray(data) ? data[0] : data);
      setUser(userData);
      setFormData(userData);
    } catch (err) {
      console.error('Error fetching user:', err);
      toast.error('Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserBadges = async () => {
    try {
      const [userBadgesRes, allBadgesRes] = await Promise.all([
        automunityAPI.getUserBadges(id!),
        automunityAPI.getBadges()
      ]);
      const userBadgesData = await userBadgesRes.body.json();
      const allBadgesData = await allBadgesRes.body.json();

      const userBadgeList = Array.isArray(userBadgesData) ? userBadgesData
        : Array.isArray((userBadgesData as any)?.records) ? (userBadgesData as any).records : [];
      const badgeList = Array.isArray(allBadgesData) ? allBadgesData
        : Array.isArray((allBadgesData as any)?.records) ? (allBadgesData as any).records : [];

      // Join user_badge records with badge details, sort by date_entered
      const enriched: Badge[] = userBadgeList
        .map((ub: any) => {
          const badge = badgeList.find((b: any) => b.badge_id === ub.badge_id);
          return badge ? { ...badge, date_entered: ub.date_entered } : null;
        })
        .filter(Boolean)
        .sort((a: any, b: any) => {
          if (!a.date_entered) return 1;
          if (!b.date_entered) return -1;
          return new Date(a.date_entered).getTime() - new Date(b.date_entered).getTime();
        });
      setBadges(enriched);
    } catch (err) {
      console.error('Error fetching badges:', err);
    }
  };

  const handleSave = async () => {
    try {
      await automunityAPI.updateUser(id!, {
        full_name: formData.full_name,
        email: formData.email
      });
      toast.success('User updated successfully');
      setEditMode(false);
      fetchUser();
    } catch (err) {
      console.error('Error updating user:', err);
      toast.error('Failed to update user');
    }
  };

  if (loading) return <div className="loading">Loading user...</div>;
  if (!user) return <div className="error">User not found</div>;

  return (
    <>
      <Toaster position="top-right" />
      <div className="settings-page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1>User Profile</h1>
          <div>
            <button onClick={() => navigate('/users')} className="btn btn-secondary" style={{ marginRight: '10px' }}>Back</button>
            <button onClick={() => setEditMode(!editMode)} className="btn btn-primary">
              {editMode ? 'Cancel' : 'Edit'}
            </button>
          </div>
        </div>

        <div className="portlet">
          <div className="portlet-body">
            {editMode ? (
              <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                <div className="form-group">
                  <label>Full Name</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={formData.full_name || ''} 
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} 
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    value={formData.email || ''} 
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                  />
                </div>
                <button type="submit" className="btn btn-success">Save Changes</button>
              </form>
            ) : (
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <strong>ID:</strong> {user.user_id}
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <strong>Full Name:</strong> {user.full_name}
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <strong>Email:</strong> {user.email}
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <strong>Active:</strong> {user.is_active ? 'Yes' : 'No'}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="portlet" style={{ marginTop: '20px' }}>
          <div className="portlet-body">
            <h2 style={{ marginBottom: '16px' }}>🏅 Badges</h2>
            {badges.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px' }}>
                {badges.map((badge, i) => (
                  <div key={i} style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center',
                    border: '1px solid #e9ecef',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
                  >
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>{badge.icon || '🏅'}</div>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b', marginBottom: '4px' }}>{badge.name}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>{badge.description}</div>
                    {badge.date_entered && (
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                        Earned {new Date(badge.date_entered).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>🎯</div>
                <p>No badges earned yet. Keep contributing to automations to unlock achievements!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfile;
